import { Grid, Button, Divider } from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import { ReactComponent as ResetIcon } from 'assets/images/reset-icon.svg'
import { APP_COLORS } from 'styles/appColors'

import { ColorPicker } from '../ColorPicker'
import { useStyles } from '../styles'
import { Style } from '../types'
import { ImageUploader } from '../ImageUploader'

interface AppMobileProps {
  style: Style
  setStyle: (style: Style) => void
  setLogoFile: (file: File | undefined) => void
  setHomeFile: (file: File | undefined) => void
  onSave: () => void
}

const AppMobile: React.FC<AppMobileProps> = ({
  style,
  setStyle,
  setLogoFile,
  setHomeFile,
  onSave,
}) => {
  const classes = useStyles()
  const [isResetting, setIsResetting] = useState(false)

  const [primaryBackground, setPrimaryBackground] = useState<string>(
    style.appColors.$PRIMARY_BACKGROUND,
  )
  const [secondaryBackground, setSecondaryBackground] = useState<string>(
    style.appColors.$SECONDARY_BACKGROUND,
  )
  const [logoPreview, setLogoPreview] = useState<string>(style.appLogoUrl)
  const [homePreview, setHomePreview] = useState<string>(style.homeUrl)

  useEffect(() => {
    if (isResetting) {
      onSave()
      setIsResetting(false)
    }
  }, [style, isResetting, onSave])

  useEffect(() => {
    setPrimaryBackground(style.appColors.$PRIMARY_BACKGROUND)
    setSecondaryBackground(style.appColors.$SECONDARY_BACKGROUND)
    setLogoPreview(style.appLogoUrl)
    setHomePreview(style.homeUrl)
  }, [style])

  const handlePrimaryBackgroundChange = (color: string) => {
    setPrimaryBackground(color)
    setStyle({
      ...style,
      appColors: {
        ...style.appColors,
        $PRIMARY_BACKGROUND: color,
      },
    })
  }

  const handleSecondaryBackgroundChange = (color: string) => {
    setSecondaryBackground(color)
    setStyle({
      ...style,
      appColors: {
        ...style.appColors,
        $SECONDARY_BACKGROUND: color,
      },
    })
  }

  const handleLogoChange = (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setLogoPreview(previewUrl)
    setLogoFile(file)
    setStyle({
      ...style,
      appLogoUrl: previewUrl,
    })
  }

  const handleLogoDelete = () => {
    setLogoPreview('')
    setLogoFile(undefined)
    setStyle({
      ...style,
      appLogoUrl: '',
    })
  }

  const handleHomeChange = (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setHomePreview(previewUrl)
    setHomeFile(file)
    setStyle({
      ...style,
      homeUrl: previewUrl,
    })
  }

  const handleHomeDelete = () => {
    setHomePreview('')
    setHomeFile(undefined)
    setStyle({
      ...style,
      homeUrl: '',
    })
  }

  const reset = () => {
    // Primero actualizamos los estados locales
    setPrimaryBackground(APP_COLORS.$PRIMARY_BACKGROUND)
    setSecondaryBackground(APP_COLORS.$SECONDARY_BACKGROUND)
    setLogoPreview('')
    setHomePreview('')
    setLogoFile(undefined)
    setHomeFile(undefined)

    // Luego actualizamos el estado del padre
    const newStyle = {
      ...style,
      appColors: {
        $PRIMARY_BACKGROUND: APP_COLORS.$PRIMARY_BACKGROUND,
        $SECONDARY_BACKGROUND: APP_COLORS.$SECONDARY_BACKGROUND,
      },
      appLogoUrl: '',
      homeUrl: '',
    }

    setStyle(newStyle)
    setIsResetting(true)
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Button
          onClick={reset}
          variant="outlined"
          color="primary"
          className={classes.resetButton}
          startIcon={<ResetIcon className={classes.resetIcon} />}
        >
          Restaurar valores por defecto
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Colores</div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Primario"
          color={primaryBackground}
          handler={handlePrimaryBackgroundChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <ColorPicker
          label="Secundario"
          color={secondaryBackground}
          handler={handleSecondaryBackgroundChange}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>
      <Grid item xs={12}>
        <div className={classes.dividerContainer}>
          <div className={classes.dividerText}>Imágenes</div>
        </div>
      </Grid>
      <Grid item xs={12} sm={6}>
        <ImageUploader
          label="Logo"
          preview={logoPreview}
          onChange={handleLogoChange}
          onDelete={handleLogoDelete}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ImageUploader
          label="Imagen de inicio"
          preview={homePreview}
          onChange={handleHomeChange}
          onDelete={handleHomeDelete}
        />
      </Grid>
    </Grid>
  )
}

export { AppMobile }
